import React, { useState } from "react";

// icons
import { FiSearch } from "react-icons/fi";
import {
  MdPersonAddAlt1,
  MdVisibility,
  MdArrowUpward,
  MdArrowDownward,
  MdGroups,
} from "react-icons/md";
import Card from "../../../../components/card";
import ModalAddUser from "./ModalAddUser";
import ModalViewDetailUser from "./ModalViewDetailUser";
import api from "../../../../services/axios";
import SkeletonTable from "../../../../components/skeleton/table";
import { toast } from "react-toastify";

interface TableColumn {
  key: string;
  header: string;
}

interface TableRow {
  [key: string]: string | number;
}

interface ResponsiveTableProps {
  columns: TableColumn[];
  data: TableRow[];
}

const selectCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const formatCell = (key: string, value: string | number) => {
  if (value == null || value === "") return "—";
  if (key === "dtOfBirth") {
    try {
      return new Date(value as string).toLocaleDateString("pt-BR");
    } catch {
      return value;
    }
  }
  return value;
};

const UserManagement: React.FC<ResponsiveTableProps> = ({ columns, data }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [openAddUserModal, setOpenAddUserModal] = useState<boolean>(false);
  const [openUserDetailModal, setUserDetailModal] = useState<boolean>(false);
  const [userDetail, setUserDetail] = useState<object | null>(null);
  const [userData, setUserData] = useState<TableRow[]>(data);
  const [loading, setLoading] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Ativa/inativa o usuário direto na lista (sem abrir o modal). Atualização otimista
  // com rollback em caso de erro. Envia só o flActive (o backend faz update parcial).
  const handleToggleActive = async (row: TableRow) => {
    const id = Number(row.id);
    const current = Boolean(row.flActive);
    const next = !current;

    setTogglingId(id);
    setUserData((prev) =>
      prev.map((r) =>
        Number(r.id) === id ? ({ ...r, flActive: next } as unknown as TableRow) : r
      )
    );

    try {
      await api.put(`/Employee/employees/${id}`, { flActive: next });
      toast.success(next ? "Usuário ativado" : "Usuário inativado");
    } catch (error) {
      console.error("Erro ao atualizar status do usuário:", error);
      setUserData((prev) =>
        prev.map((r) =>
          Number(r.id) === id ? ({ ...r, flActive: current } as unknown as TableRow) : r
        )
      );
      toast.error("Erro ao atualizar status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSort = (columnKey: string) => {
    setSortedColumn(columnKey);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredData = userData.filter((row) =>
    Object.values(row).some((value) =>
      value
        ? value.toString().toLowerCase().includes(searchTerm?.toLowerCase())
        : ""
    )
  );

  // Ativos sempre primeiro; dentro de cada grupo aplica a ordenação por coluna (ou ordem natural).
  const sortedData = [...filteredData].sort((a, b) => {
    const byActive = Number(Boolean(b.flActive)) - Number(Boolean(a.flActive));
    if (byActive !== 0) return byActive;
    if (!sortedColumn) return 0;
    return sortOrder === "asc"
      ? a[sortedColumn] > b[sortedColumn]
        ? 1
        : -1
      : a[sortedColumn] < b[sortedColumn]
      ? 1
      : -1;
  });

  const handleRowClick = (row: TableRow) => {
    setUserDetail(row);
    setUserDetailModal(true);
  };

  const handleOpenModal = () => {
    setOpenAddUserModal(true);
  };

  const getDetails = async () => {
    setLoading(true);
    function fetchClients() {
      api
        .get("/Employee/employees")
        .then((response) => {
          setUserData(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("There was an error!", error);
          setLoading(true);
        });
    }
    fetchClients();
  };

  return (
    <div>
      {loading ? (
        <SkeletonTable active={loading} />
      ) : (
        <div className="animate-fade-up">
          <Card extra="p-5">
            {/* Toolbar header: add action */}
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={handleOpenModal}
                className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98]"
              >
                <MdPersonAddAlt1 className="h-4 w-4" aria-hidden="true" />
                Adicionar
              </button>
            </div>

            {/* Toolbar: search */}
            <div className="mb-5 flex flex-col gap-1.5">
              <label
                htmlFor="users-search"
                className="text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                Buscar
              </label>
              <div className="relative">
                <FiSearch
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="users-search"
                  type="text"
                  placeholder="Buscar por nome, e-mail, cargo…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`${selectCls} pl-9`}
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-[0_8px_30px_rgba(112,144,176,0.12)] dark:border-white/10 dark:bg-navy-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-navy-900">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        className="cursor-pointer select-none px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                        onClick={() => handleSort(column.key)}
                      >
                        <span className="inline-flex items-center gap-1">
                          {column.header}
                          {sortedColumn === column.key &&
                            (sortOrder === "asc" ? (
                              <MdArrowUpward
                                className="h-3.5 w-3.5 text-dv-blue-500"
                                aria-hidden="true"
                              />
                            ) : (
                              <MdArrowDownward
                                className="h-3.5 w-3.5 text-dv-blue-500"
                                aria-hidden="true"
                              />
                            ))}
                        </span>
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`px-4 py-3 ${
                            column.key === "id" || column.key === "dtOfBirth"
                              ? "tabular-nums"
                              : ""
                          } ${
                            column.key === "userName"
                              ? "font-semibold text-navy-700 dark:text-white"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {formatCell(column.key, row[column.key])}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          disabled={togglingId === Number(row.id)}
                          aria-label={
                            Boolean(row.flActive) ? "Inativar usuário" : "Ativar usuário"
                          }
                          title={
                            Boolean(row.flActive)
                              ? "Ativo — clique para inativar"
                              : "Inativo — clique para ativar"
                          }
                          onClick={() => handleToggleActive(row)}
                          className="inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span
                            aria-hidden="true"
                            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                              Boolean(row.flActive)
                                ? "bg-emerald-400"
                                : "bg-gray-300 dark:bg-navy-600"
                            }`}
                          >
                            <span
                              className={`absolute h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                Boolean(row.flActive) ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            />
                          </span>
                          <span
                            className={`text-xs font-semibold ${
                              Boolean(row.flActive)
                                ? "text-emerald-600 dark:text-emerald-300"
                                : "text-gray-600 dark:text-gray-300"
                            }`}
                          >
                            {Boolean(row.flActive) ? "Ativo" : "Inativo"}
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          aria-label="Ver detalhes do usuário"
                          title="Ver detalhes"
                          onClick={() => handleRowClick(row)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:text-dv-blue-600 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
                        >
                          <MdVisibility className="h-4 w-4" aria-hidden="true" />
                          Detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sortedData.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + 2} className="px-4 py-12">
                        <div className="flex flex-col items-center justify-center gap-2 text-center">
                          <span
                            className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-navy-900 dark:text-gray-500"
                            aria-hidden="true"
                          >
                            <MdGroups className="h-6 w-6" />
                          </span>
                          <p className="text-sm font-semibold text-navy-700 dark:text-white">
                            Nenhum usuário encontrado
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300">
                            Ajuste a busca ou adicione um novo usuário.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      <ModalAddUser
        openAddUserModal={openAddUserModal}
        setOpenAddUserModal={setOpenAddUserModal}
        updateState={() => getDetails()}
      ></ModalAddUser>
      <ModalViewDetailUser
        openUserDetailModal={openUserDetailModal}
        setOpenUserDetailModal={setUserDetailModal}
        details={userDetail}
        updateState={() => getDetails()}
      ></ModalViewDetailUser>
    </div>
  );
};

export default UserManagement;
